/**
 * De opbrengst uitdelen.
 *
 * Eén taak: na een worp elk landschap met het gegooide getal een grondstof geven, tot het
 * maximum van drie, en bijhouden wat er verloren ging. `game.ts` roept dit aan tussen de
 * gebeurtenis en de hoofdfase in — die volgorde schrijft het handboek voor (p. 12) en ze
 * doet ertoe: de roofoverval kijkt naar je voorraad, en die is vóór de opbrengst kleiner.
 *
 * Dit bestand heette `moves.ts` en bevatte daarnaast een `Move`/`isLegal`/`apply` die maar
 * twee zetten kende en nergens werd gebruikt — een schets van vóór `Action`. Die leek zo
 * sterk op de echte ingang (`isLegalAction` en `perform` in `game.ts`, met `legalActions`
 * uit `zetten.ts`) dat wie er een server op aansloot, hem op het verkeerde aansloot. Weg
 * dus, en de naam erbij: wat hier staat gaat over opbrengst, niet over zetten.
 */
import { LIMITS, type ProductionRoll, type Seat } from '@catan/contracts';
import { yieldOf } from './eventcards.js';
import type { GameState, LandscapeTile, PlayerState } from './state.js';

export type GameEvent =
  | { readonly type: 'gedobbeld'; readonly event: string; readonly production: ProductionRoll }
  | {
      readonly type: 'opbrengst';
      readonly seat: Seat;
      /** Kolom en zijde van het landschap, plus hoeveel er daadwerkelijk bij kwam. */
      readonly tiles: readonly {
        readonly column: number;
        readonly side: 'boven' | 'onder';
        readonly gained: number;
        readonly wasted: number;
      }[];
    }
  | { readonly type: 'beurt-gestart'; readonly seat: Seat; readonly turnNumber: number };

export interface ApplyResult {
  readonly state: GameState;
  readonly events: readonly GameEvent[];
}

/**
 * Deelt de opbrengst uit aan één speler.
 *
 * Elk landschap met het gegooide getal krijgt er één grondstof bij, tot het maximum
 * van drie; wat daarboven zou komen vervalt (handboek p. 13, `msg 183`).
 */
function distributeYield(
  player: PlayerState,
  roll: ProductionRoll,
): { player: PlayerState; tiles: { column: number; side: 'boven' | 'onder'; gained: number; wasted: number }[] } {
  const touched: { column: number; side: 'boven' | 'onder'; gained: number; wasted: number }[] = [];

  const landscapes: LandscapeTile[] = player.principality.landscapes.map((tile) => {
    if (tile.dieNumber !== roll) return tile;
    // Een molen, gieterij, oven, spinnerij of zagerij ernaast verdubbelt de opbrengst.
    const opbrengst = yieldOf(player, tile);
    const room = LIMITS.resourcesPerLandscape - tile.resources;
    const gained = Math.min(opbrengst, Math.max(0, room));
    touched.push({
      column: tile.column,
      side: tile.side,
      gained,
      wasted: opbrengst - gained,
    });
    return gained === 0 ? tile : { ...tile, resources: tile.resources + gained };
  });

  return {
    player: { ...player, principality: { ...player.principality, landscapes } },
    tiles: touched,
  };
}

/**
 * Deelt de opbrengst uit aan beide spelers en zet de beurt in de hoofdfase.
 *
 * Wordt aangeroepen ná het afhandelen van de gebeurtenis — die volgorde schrijft het
 * handboek voor (p. 12) en ze doet ertoe: de roofoverval kijkt naar je voorraad, en
 * die is vóór de opbrengst kleiner.
 */
export function distributeProduction(state: GameState, roll: ProductionRoll): ApplyResult {
  const events: GameEvent[] = [];
  const gekregen: number[] = [];
  const players = state.players.map((player) => {
    const result = distributeYield(player, roll);
    events.push({ type: 'opbrengst', seat: player.seat, tiles: result.tiles });
    gekregen.push(result.tiles.reduce((sum, t) => sum + t.gained, 0));
    return result.player;
  }) as [PlayerState, PlayerState];

  // Alleen opbrengst uit de dobbelsteen telt mee voor de eindscore (handboek p. 8).
  const produced: [number, number] = [
    state.produced[0] + (gekregen[0] ?? 0),
    state.produced[1] + (gekregen[1] ?? 0),
  ];

  return { state: { ...state, players, produced, phase: 'hoofdfase' }, events };
}
